import { inject, onMounted, onUnmounted, provide, ref, watch } from 'vue';
import { useSocketQuery } from '@/generated/graphql';
import { type LiterallyAnything } from '@/types/utils';
import {
	type AllSocketMessages,
	SOCKET_PROVIDE_KEY,
	type SocketListeners,
} from './types';
import { useSocketListenersStore } from '@/stores/socketListeners';
import { getUniqueId } from '@/utils/misc';

/**
 * This listener needs to be only registered once since we only want to emit the
 * socket message to the monolith one time, not once for each component that
 * uses `useSocket`.
 */
const globalNodeMessageHandler = (message: MessageEvent<AllSocketMessages>) => {
	const msg = message.data;
	const event = new CustomEvent('spa-node-message', { detail: msg });
	window.dispatchEvent(event);
};

enum MessageType {
	BEHAVIOUR = 'behaviour',
	MESSAGE = 'message',
}

const buildMessage = (type: MessageType, payload: LiterallyAnything) => {
	return JSON.stringify({
		type,
		payload,
	});
};

/**
 * Keep track of the socket connection globally so that it is shared across all
 * MFEs.
 */
const socket = ref<WebSocket>();
const serverUrl = ref<string>();
const userJwt = ref<string>();
const firstRun = ref(true);

let intervalRetry = 1;
let connectionCheckingInterval: number | undefined;
let isPageUnloading = false;

/**
 * A composable that makes listening for websocket messages easier. The
 * messages and payloads are statically typed.
 *
 * Any listeners are automatically removed when the component is unmounted.
 *
 * Usage example:
 * ```ts
 * useSocket({
 *   [MessageType.MARK_NOTIFICATION_READ]: (msg) => {
 *     console.log('notification was marked as read', { msg.data.notificationId });
 *   },
 * })
 * ```
 */
export const useSocket = (listeners: SocketListeners) => {
	// Inject the socket instance, requires
	// `useCreateSocketConnection` to have been set up
	const socket = inject(SOCKET_PROVIDE_KEY);
	const socketListenersStore = useSocketListenersStore();
	const originalListeners = listeners;

	const uuid = ref<string | null>(null);

	/**
	 * The listener for handling all incoming messages. Will run the correct
	 * listener based on the message type.
	 */
	const onMessage = async (message: MessageEvent) => {
		const msg = JSON.parse(message.data) as AllSocketMessages;
		const listeners = socketListenersStore.registeredListeners[msg.type];

		if (listeners) {
			for (const listener of listeners) {
				await listener.cb(msg);
			}
		}
	};

	const attachListeners = (
		listeners: SocketListeners = originalListeners
	) => {
		uuid.value = getUniqueId();
		socketListenersStore.registerListeners(uuid.value, listeners);

		if (socket?.value) {
			socket.value.onmessage = (e) => {
				globalNodeMessageHandler(e);
				onMessage(e);
			};
		}
	};

	const removeListeners = () => {
		if (!socket?.value || !uuid.value) {
			return;
		}

		socketListenersStore.removeListeners(uuid.value);
	};

	// Ideally, when the component mounts, we want to start listening for
	// socket messages
	onMounted(attachListeners);

	watch(
		() => socket?.value,
		(newSocket, oldSocket) => {
			if (!newSocket) {
				return;
			}

			if (oldSocket) {
				oldSocket.onmessage = null;
				oldSocket.onopen = null;
				oldSocket.onclose = null;
			}

			// If a connection has been restored, attach listeners again
			newSocket.onopen = () => {
				attachListeners();
			};
		}
	);

	// Make sure to clean up all listeners when the component is unmounted
	onUnmounted(removeListeners);

	return {
		removeListeners,
		attachListeners,
	};
};

/**
 * A composable for creating the Socket instance
 * Should be used within a setup hook when initialising the Vue application
 *
 * **Note:** This needs to be used AFTER the `apolloClient` plugin has been injected.
 *
 * Usage example:
 * ```ts
 * setup() {
 * 	  provide(DefaultApolloClient, apollo);
 *    useCreateSocketConnection();
 * }
 * ```
 */
export const useCreateSocketConnection = () => {
	// The options are are made like because apollo calls
	// the onResult twice, once for the cache and once for the refetch
	// triggering the connect to the socket twice on every single try
	const pathname = window.location.pathname || '';
	const modulePath = pathname.split('/')[1] || '';
	const { onResult, refetch } = useSocketQuery({
		moduleName: modulePath,
	});

	const dispatchSocketLoadedEvent = () => {
		window.dispatchEvent(new CustomEvent('spa-socket-loaded'));
	};

	const connect = (): WebSocket | undefined => {
		if (!serverUrl.value) {
			return;
		}

		return new WebSocket(serverUrl.value, userJwt.value);
	};

	const reconnect = () => {
		if (
			(socket.value &&
				socket.value.readyState !== WebSocket.OPEN &&
				socket.value.readyState !== WebSocket.CONNECTING &&
				!isPageUnloading) ||
			(!socket.value && !isPageUnloading)
		) {
			refetch();
		}
	};

	const startConnectionChecking = () => {
		connectionCheckingInterval = window.setInterval(() => {
			clearInterval(connectionCheckingInterval);
			if (socket.value) {
				socket.value.onclose = null;
			}

			if (
				socket.value?.readyState === WebSocket.OPEN ||
				intervalRetry > 10
			) {
				return;
			}

			intervalRetry++;
			reconnect();
			startConnectionChecking();
		}, 1000 * intervalRetry);
	};

	onResult((result) => {
		if (!result.data) {
			return;
		}
		// If onResult has already been called then socket.value exists
		if (
			(socket.value &&
				(socket.value.readyState === WebSocket.OPEN ||
					socket.value.readyState === WebSocket.CONNECTING)) ||
			intervalRetry > 10
		) {
			return;
		}

		serverUrl.value = result.data.SocketConnection.serverUrl;
		userJwt.value = result.data.SocketConnection.userToken;
		socket.value = connect();

		// We do not need to set up listeners again
		if (firstRun.value && socket.value) {
			firstRun.value = false;

			socket.value.addEventListener('open', dispatchSocketLoadedEvent);

			socket.value.onclose = () => {
				// Not trigger the onopen again
				if (socket.value) {
					socket.value.removeEventListener(
						'open',
						dispatchSocketLoadedEvent
					);
					socket.value.onopen = null;
				}
				startConnectionChecking();
			};

			/**
			 * Reconnect the socket when the browser/tab is re-focused.
			 */
			const onVisibilityChanged = () => {
				const isVisible =
					!document.hidden || document.visibilityState === 'visible';
				const isDisconnected =
					socket.value?.readyState === WebSocket.CLOSED;
				if (isVisible && isDisconnected) {
					reconnect();
				}
			};

			const sendSocketMessage = (
				data: string | ArrayBufferLike | Blob | ArrayBufferView
			) => {
				if (socket.value?.readyState === WebSocket.OPEN) {
					socket.value.send(data);
				}
			};

			const handleScoroBaseNodeMessage = (
				e: CustomEvent<LiterallyAnything>
			) => {
				const payload = e.detail;
				sendSocketMessage(
					buildMessage(MessageType.MESSAGE, {
						type: payload.type,
						data: payload.data,
					})
				);
			};

			const customEventListener = ((event: CustomEvent) => {
				handleScoroBaseNodeMessage(event);
			}) as EventListener;

			const sendBehaviourData = (
				data: CustomEvent<LiterallyAnything>
			) => {
				sendSocketMessage(
					buildMessage(MessageType.BEHAVIOUR, data.detail)
				);
			};

			const customBehaviourDataListener = ((event: CustomEvent) => {
				sendBehaviourData(event);
			}) as EventListener;

			window.addEventListener('beforeunload', () => {
				isPageUnloading = true;
				// Have to close connection manually so that automatic reconnect is not triggered
				socket.value?.close();

				document.removeEventListener(
					'visibilitychange',
					onVisibilityChanged
				);
				window.removeEventListener(
					'scoro-base-node-message',
					customEventListener
				);
				window.removeEventListener(
					'scoro-base-behaviour-data',
					customBehaviourDataListener
				);
			});

			document.addEventListener('visibilitychange', onVisibilityChanged);
			window.addEventListener(
				'scoro-base-node-message',
				customEventListener
			);
			window.addEventListener(
				'scoro-base-behaviour-data',
				customBehaviourDataListener
			);
		}
	});

	provide(SOCKET_PROVIDE_KEY, socket);
};
